 
// export default TransactionStartModal;

import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { ntxAPI } from '@API/axiosNeatex';
import { Card } from '@components/card/card';

// مكونات الجداول المنفصلة
import { PurchaseOrderTable } from './PurchaseOrderTable';
import { SalesInvoiceTable } from './SalesInvoiceTable';
import { SalesReturnTable } from './SalesReturnTable';
import { TransferReceiptTable } from './TransferReceiptTable';
import { ReasonTable } from './ReasonTable';
import { MoveOrderTable } from './MoveOrderTable';
import { WorkOrderTable } from './WorkOrderTable';
import { DefaultTable } from './DefaultTable';
import { SupplirReturnTable } from './SupplirReturnTable';
import TransactionUnavailable from './TransactionUnavailable';

export type Option = {
  value: string;
  label: string;
  operatingUnitId?: string;
  isDisabled?: boolean;
  transTypeId?: string;
};

type ConfirmPayload = {
  branchId: string;
  inventoryId: string;
  code: string;
  referenceId: string | null;
  referenceCode: string;
};

type Props = {
  show: boolean;
  onCancel: () => void;
  onConfirm: (payload: ConfirmPayload) => void;

  OperatingUnitOptions: Option[];
  InventoryOptions: Option[];
  OptionsTransactionTypes: Option[];

  loading?: boolean;
  error?: any;

  requireReference?: boolean;
  initial?: Partial<ConfirmPayload>;

  // header customization
  headerTitle?: string;
  headerSubtitle?: string;
  headerIconClassName?: string; // مثال: "fas fa-dolly" أو "fas fa-arrow-up"
};

const TransactionStartModal: React.FC<Props> = ({
  show,
  onCancel,
  onConfirm,
  OperatingUnitOptions,
  InventoryOptions,
  OptionsTransactionTypes,
  loading = false,
  error = null,
  requireReference = true,
  initial,
  headerSubtitle,
  headerIconClassName,
  headerTitle
}) => {
  const { t, i18n } = useTranslation();

  const modalTitle = headerTitle ?? t('InventoryTransactionStartTitle');
  const modalSubtitle = headerSubtitle ?? t('InventoryTransactionStartSubtitle');
  const modalIcon = headerIconClassName ?? 'fas fa-warehouse';

  // Local selections
  const [branchId, setBranchId] = useState(initial?.branchId ?? '');
  const [inventoryId, setInventoryId] = useState(initial?.inventoryId ?? '');
  const [code, setCode] = useState(initial?.code ?? '');

  // Reference state
  const [refOptions, setRefOptions] = useState<any[]>([]);
  const [selectedReferenceId, setSelectedReferenceId] = useState<string | null>(initial?.referenceId ?? null);
  const [selectedReferenceCode, setSelectedReferenceCode] = useState(initial?.referenceCode ?? '');
  const [refLoading, setRefLoading] = useState(false);
  const [refLoaded, setRefLoaded] = useState(false);
  const [refError, setRefError] = useState<string | null>(null);

  // For tracking selected transaction type
  const [selectedTransactionType, setSelectedTransactionType] = useState<Option | any>(null);

  // Re-init on open
  useEffect(() => {
    if (!show) return;

    setBranchId(initial?.branchId ?? '');
    setInventoryId(initial?.inventoryId ?? '');
    setCode(initial?.code ?? '');

    const initialType = initial?.code ? OptionsTransactionTypes.find(t => t.value === initial.code) : null;
    setSelectedTransactionType(initialType);

    setRefOptions([]);
    setRefError(null);
    setRefLoaded(false);
    setSelectedReferenceId(initial?.referenceId ?? null);
    setSelectedReferenceCode(initial?.referenceCode ?? '');
  }, [show]);

  // Filter inventories by OU if available
  const filteredInventories = useMemo(() => {
    if (!branchId) return [];
    const hasOU = InventoryOptions?.some(x => x.operatingUnitId != null);
    return hasOU ? InventoryOptions.filter(x => x.operatingUnitId === branchId) : InventoryOptions;
  }, [InventoryOptions, branchId]);

  // Icons for different transaction types
  const getTransactionTypeIcon = (typeCode: string) => {
    switch (typeCode) {
      case 'RFG':
        return <i className='fas fa-box-open fa-2x text-success' title={t('TransType_RFG_Title')}></i>;
      case 'MOR':
        return <i className='fas fa-truck-loading fa-2x text-primary' title={t('TransType_MOR_Title')}></i>;
      case 'MIR':
        return <i className='fas fa-plus-circle fa-2x text-info' title={t('TransType_MIR_Title')}></i>;
      case 'POR':
        return <i className='fas fa-shopping-cart fa-2x text-warning' title={t('TransType_POR_Title')}></i>;
      case 'CRT':
        return <i className='fas fa-undo-alt fa-2x text-danger' title={t('TransType_CRT_Title')}></i>;
      case 'CRP':
        return <i className='fas fa-undo fa-2x text-purple' title={t('TransType_CRP_Title')}></i>;
      case 'INV':
        return <i className='fas fa-file-invoice fa-2x text-cyan' title={t('TransType_INV_Title')}></i>;
      case 'SOI':
        return <i className='fas fa-clipboard-list fa-2x text-lime' title={t('TransType_SOI_Title')}></i>;
      case 'SOP':
        return <i className='fas fa-cash-register fa-2x text-orange' title={t('TransType_SOP_Title')}></i>;
      case 'MOI':
        return <i className='fas fa-exchange-alt fa-2x text-indigo' title={t('TransType_MOI_Title')}></i>;
      case 'SPR':
        return <i className='fas fa-arrow-left fa-2x text-teal' title={t('TransType_SPR_Title')}></i>;
      case 'SRP':
        return <i className='fas fa-arrow-left fa-2x text-sky' title={t('TransType_SRP_Title')}></i>;
      case 'BIL':
        return <i className='fas fa-file-invoice-dollar fa-2x text-violet' title={t('TransType_BIL_Title')}></i>;
      case 'IOR':
        return <i className='fas fa-dolly fa-2x text-emerald' title={t('TransType_IOR_Title')}></i>;
      case 'WOI':
        return <i className='fas fa-wrench fa-2x text-stone' title={t('TransType_WOI_Title')}></i>;
      case 'CCA':
        return <i className='fas fa-tools fa-2x text-yellow' title={t('TransType_CCA_Title')}></i>;
      case 'MII':
        return <i className='fas fa-plus fa-2x text-blue' title={t('TransType_MII_Title')}></i>;
      default:
        return <i className='fas fa-exchange-alt fa-2x text-secondary'></i>;
    }
  };

  const getTransactionTypeDescription = (typeCode: string) => {
    switch (typeCode) {
      case 'RFG':
        return t('TransType_RFG_Title');
      case 'MOR':
        return t('TransType_MOR_Title');
      case 'MIR':
        return t('TransType_MIR_Title');
      case 'POR':
        return t('TransType_POR_Title');
      case 'CRT':
        return t('TransType_CRT_Title');
      case 'CRP':
        return t('TransType_CRP_Title');
      case 'INV':
        return t('TransType_INV_Title');
      case 'SOI':
        return t('TransType_SOI_Title');
      case 'SOP':
        return t('TransType_SOP_Title');
      case 'MOI':
        return t('TransType_MOI_Title');
      case 'SPR':
        return t('TransType_SPR_Title');
      case 'SRP':
        return t('TransType_SRP_Title');
      case 'BIL':
        return t('TransType_BIL_Title');
      case 'IOR':
        return t('TransType_IOR_Title');
      case 'WOI':
        return t('TransType_WOI_Title');
      case 'CCA':
        return t('TransType_CCA_Title');
      case 'MII':
        return t('TransType_MII_Title');
      default:
        return '';
    }
  };

  const handleTransactionTypeSelect = (type: Option) => {
    setSelectedTransactionType(type);
    setCode(type.value);
    // Reset other selections
    setBranchId('');
    setInventoryId('');
    setSelectedReferenceId(null);
    setSelectedReferenceCode('');
    setRefOptions([]);
    setRefLoaded(false);
  };

  const handleBackToSelection = () => {
    setSelectedTransactionType(null);
    setCode('');
    setBranchId('');
    setInventoryId('');
    setSelectedReferenceId(null);
    setSelectedReferenceCode('');
    setRefOptions([]);
    setRefLoaded(false);
  };

  const getReferenceUrl = (): string | null => {
    switch (code) {
      case 'INV':
        return `SalesInvoice/GetTransactionsInvoiceListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'SOI':
        return `SalesOrderSalse/GetTransactionsOrderSalseListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'SOP':
        return `Pos/GetPOSTransactionsOrderSalseListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}`;
      case 'CRP':
        return `SalesReturns/GetPOSTransactionsReturnsListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}`;
      case 'MOI':
        return `InvMove/GetTransactionsMoveListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'SPR':
        return `ReturnsPurchase/GetListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'SRP':
        return `ReturnsPurchaseOrder/GetListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'BIL':
        return `PurchaseBill/GetListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'POR':
        return `PurchaseOrder/GetListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'CRT':
        return `SalesReturns/GetTransactionsReturnsListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'IOR':
      case 'MOR': // استلام امر تحويــل
      return `InvIssue/GetTransactionsIssueListByInventoryToIdAndInventoryFlag?InventoryToId=${inventoryId}&InventoryFlag=false`;
        // return `InvIssue/GetTransactionsIssueListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'WOI':
        return `Fleet/GetWorkOrderListByInventoryIdAndInventoryFlag?InventoryId=${inventoryId}&InventoryFlag=false`;
      case 'CCA':
      case 'MIR':
        return `Inv/GetInvTransactionTypeReasonsByTransactionTypeId?TransactionTypeId=fc437ea7-caf1-46ad-85b3-9dea0c5accdb`;
      case 'MII':
        return `Inv/GetInvTransactionTypeReasonsByTransactionTypeId?TransactionTypeId=24e295ae-15d7-4f85-ad43-e7cfa4f8e9ad`;
      default:
        return null;
    }
  };

  const fetchReferences = async () => {
    if (!branchId || !inventoryId || !code) return;

    const url = getReferenceUrl();
    if (!url) {
      setRefOptions([]);
      setRefLoaded(true);
      setRefError(null);
      return;
    }

    setRefLoading(true);
    setRefError(null);

    try {
      const res = await ntxAPI.get(url);

      if (!res?.data?.succeeded) {
        setRefOptions([]);
        setRefLoaded(false);
        setRefError('Error');
        return;
      }

      const list: any[] = res.data.data ?? [];

      // Filter for specific types if needed
      let filteredList = list;
      if (code === 'IOR') {
        filteredList = list.filter(item => item.transactionTypeId === 'ae3fe2fd-f2cb-44f3-9ae2-a01e466df8b6');
      }

      setRefOptions(filteredList);
      setRefLoaded(true);
      setRefError(null);

      // إذا كانت هناك قيمة ابتدائية، حددها
      if (initial?.referenceId && filteredList.some(item => item.id === initial.referenceId)) {
        setSelectedReferenceId(initial.referenceId);
        const selectedItem = filteredList.find(item => item.id === initial.referenceId);
        setSelectedReferenceCode(selectedItem?.code || selectedItem?.name || '');
      }
    } catch (e) {
      setRefOptions([]);
      setRefLoaded(false);
      setRefError('Error');
    } finally {
      setRefLoading(false);
    }
  };

  const handleReferenceSelect = (id: string, codeValue: string) => {
    setSelectedReferenceId(id);
    setSelectedReferenceCode(codeValue);
  };

  const canContinue =
    !!branchId && !!inventoryId && !!code && (!requireReference || (refLoaded && !!selectedReferenceId));

  const canFetch = !!branchId && !!inventoryId && !refLoading;

  // Get the appropriate table component based on transaction type
  const getTableComponent = () => {
    switch (code) {
      case 'POR':
        return (
          <PurchaseOrderTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
          />
        );
      case 'SRP': //  مردود مشتريات امر شراء
      case 'SPR': // مردود مشتريات
        return (
          <SupplirReturnTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
          />
        );
      case 'INV':
      case 'SOP': // أمر بيع نقاط بيـــع
      case 'SOI': // أوامر البيــع
        return (
          <SalesInvoiceTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
          />
        );
      case 'CRT':
      case 'CRP':
        return (
          <SalesReturnTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
          />
        );
      case 'IOR':
      case 'MOR':
        return (
          <TransferReceiptTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
          />
        );
      case 'MIR':
      case 'MII':
      case 'CCA':
        return (
          <ReasonTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
          />
        );
      case 'MOI':
        return (
          <MoveOrderTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
          />
        );
      case 'WOI':
        return (
          <WorkOrderTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
          />
        );
      case 'IRM':
        return <TransactionUnavailable />;
      default:
        return (
          <DefaultTable
            data={refOptions}
            selectedId={selectedReferenceId}
            onSelect={handleReferenceSelect}
            isLoading={refLoading}
            language={i18n.language}
            transactionType={code}
          />
        );
    }
  };

  const getTransactionTypeName = () => {
    switch (code) {
      case 'POR':
        return t('TransTypeName_POR');
      case 'INV':
        return t('TransTypeName_INV');
      case 'CRT':
        return t('TransTypeName_CRT');
      case 'CRP':
        return t('TransTypeName_CRP');
      case 'MOR':
        return t('TransTypeName_MOR');
      case 'IOR':
        return t('TransTypeName_IOR');
      case 'BIL':
        return t('TransTypeName_BIL');
      case 'MIR':
        return t('TransTypeName_MIR');
      case 'MII':
        return t('TransTypeName_MII');
      case 'CCA':
        return t('TransTypeName_CCA');
      case 'SOI':
        return t('TransTypeName_SOI');
      case 'SOP':
        return t('TransTypeName_SOP');
      case 'MOI':
        return t('TransTypeName_MOI');
      case 'WOI':
        return t('TransTypeName_WOI');
      default:
        return t('ReferencesTitle');
    }
  };

 
  return (
    // <Modal show={show} backdrop='static' keyboard={false} centered className='modal-selection-theme' size='xl'>
    <Modal show={show} backdrop='static' keyboard={false} className='modal-selection-theme' size='xl'>
      <Modal.Header className='bg-theme bg-opacity-10 text-theme border-bottom py-3'>
        <div className='d-flex align-items-center w-100'>
          <div className='bg-theme bg-opacity-10 rounded-3 p-3 me-3'>
            <i className={`${modalIcon} fa-lg text-theme`}></i>
          </div>
          <div>
            <h4 className='mb-1 text-theme'>{modalTitle}</h4>
            <p className='text-muted small mb-0'>{modalSubtitle}</p>
          </div>
        </div>
        <button type='button' className='btn-close' onClick={onCancel} aria-label='Close' title={t('Close')}></button>
      </Modal.Header>

      <Modal.Body className='p-2 min-vh-75'>
        <Loading loading={loading} error={error} Type='Dots'>
          {!selectedTransactionType ? (
            // Step 1: Transaction Type Selection
            <div className='row g-3 my-3'>
              <div className='col-lg-12'>
                <div className='form-group'>
                  <div className='d-flex flex-wrap gap-3 justify-content-center'>
                    {OptionsTransactionTypes?.map((item, idx) => (
                      <div key={idx} className='flex-grow-1' style={{ minWidth: '160px', maxWidth: '200px' }}>
                        <Card className='h-100 customcard transition-all'>
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleTransactionTypeSelect(item)}
                            role='button'
                            tabIndex={0}
                            onKeyPress={e => e.key === 'Enter' && handleTransactionTypeSelect(item)}
                          >
                            <div className='card-body text-center p-3 d-flex flex-column'>
                              <div className='mb-3 flex-grow-1 d-flex align-items-center justify-content-center'>
                                {getTransactionTypeIcon(item.value)}
                              </div>
                              <h6 className='card-title mb-2 fw-bold' style={{ fontSize: '0.9rem' }}>
                                {item.label}
                              </h6>
                              <p
                                className='card-text small text-muted mb-3 flex-grow-1'
                                style={{ fontSize: '0.75rem' }}
                              >
                                {/* {getTransactionTypeDescription(item.value)} */}
                              </p>
                            </div>
                          </div>
                        </Card>
                      </div>
                    ))}
                  </div>

                  {/* Empty State */}
                  {OptionsTransactionTypes.filter(f => !f.isDisabled)?.length === 0 && (
                    <div className='text-center py-5'>
                      <i className='fas fa-exchange-alt fa-3x text-muted mb-3'></i>
                      <h5 className='text-muted mb-2'>{t('NoTransactionTypesAvailable')}</h5>
                      <p className='text-muted'>{t('CheckPermissionsOrSettings')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Step 2: Show Operating Unit and Inventory Selection
            <div>
              {/* Header with back button */}
              <div className='d-flex justify-content-between align-items-center mb-4 mt-2 p-2 shadow-sm rounded'>
                <button className='btn btn-outline-secondary btn-sm' onClick={handleBackToSelection}>
                  <i className='fas fa-arrow-right me-2'></i>
                  {t('BackToTransactionTypeSelection')}
                </button>

                <div className='d-flex align-items-center bg-theme bg-opacity-10 px-3 py-2 rounded'>
                  <div className='me-3'>{getTransactionTypeIcon(selectedTransactionType.value)}</div>
                  <div>
                    <h6 className='mb-0 text-theme fw-bold'>{selectedTransactionType.label}</h6>
                    {/* <small className='text-muted'>
                      {getTransactionTypeDescription(selectedTransactionType.value)}
                    </small> */}
                  </div>
                </div>
              </div>

              {/* وحدة التشغيل والمخزن وزر جلب البيانات في صف واحد */}
              <div className='row g-3 mb-4'>
                {/* وحدة التشغيل */}
                <div className='col-lg-5'>
                  <div className='form-group'>
                    <label className='form-label fw-bold'>{t('OperatingUnit')}</label>
                    <Select
                      isSearchable={true}
                      isClearable
                      options={OperatingUnitOptions}
                      value={OperatingUnitOptions?.find((f: any) => f.value === branchId)}
                      onChange={(option: any) => {
                        setBranchId(option?.value || '');
                        setInventoryId('');
                        setSelectedReferenceId(null);
                        setSelectedReferenceCode('');
                        setRefOptions([]);
                        setRefLoaded(false);
                      }}
                      placeholder={t('SelectOperatingUnitPlaceholder')}
                      className='react-select-container'
                      classNamePrefix='react-select'
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </div>
                </div>

                {/* المخزن */}
                <div className='col-lg-5'>
                  <div className='form-group'>
                    <label className='form-label fw-bold'>{t('Inventory')}</label>
                    <Select
                      isSearchable={true}
                      isClearable
                      options={filteredInventories}
                      value={filteredInventories?.find((f: any) => f.value === inventoryId)}
                      onChange={(option: any) => {
                        setInventoryId(option?.value || '');
                        setSelectedReferenceId(null);
                        setSelectedReferenceCode('');
                        setRefOptions([]);
                        setRefLoaded(false);
                      }}
                      placeholder={t('SelectInventoryPlaceholder')}
                      className='react-select-container'
                      classNamePrefix='react-select'
                      isDisabled={!branchId}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </div>
                </div>

                {/* زر جلب البيانات */}
                <div className='col-lg-2'>
                  <div className='form-group h-100 d-flex flex-column justify-content-end'>
                    <button
                      disabled={!canFetch || refLoading}
                      type='button'
                      className='btn btn-outline-theme w-100 py-2'
                      onClick={fetchReferences}
                    >
                      {refLoading ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2'></span>
                          {t('FetchingData')}
                        </>
                      ) : (
                        <>
                          <i className='fas fa-download me-2'></i>
                          {t('FetchData')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* رسائل الخطأ */}
              {refError && (
                <div className='alert alert-danger mb-3'>
                  <i className='fas fa-exclamation-triangle me-2'></i>
                  {t('ReferenceFetchErrorMessage')}
                </div>
              )}

              {/* Results Table - مكون الجدول المناسب حسب النوع */}
              {refLoaded && refOptions.length > 0 && <div className='mt-4'>{getTableComponent()}</div>}

              {/* لا توجد بيانات */}
              {refLoaded && refOptions.length === 0 && (
                <div className='text-center py-5'>
                  <i className='fas fa-search fa-3x text-muted mb-3'></i>
                  <h5 className='text-muted mb-2'>{t('NoReferenceDataTitle')}</h5>
                  <p className='text-muted'>{t('NoReferenceDataDescription')}</p>
                </div>
              )}
            </div>
          )}
        </Loading>
      </Modal.Body>

      <Modal.Footer className='justify-content-end border-0 p-2'>
        {selectedTransactionType && (
          <>
            <button className='btn btn-outline-secondary btn-lg rounded-0 px-4 me-3' onClick={onCancel}>
              <i className='fas fa-times me-2'></i>
              {t('Cancel')}
            </button>

            <button
              className='btn btn-primary btn-lg rounded-0 px-5'
              disabled={!canContinue}
              onClick={() =>
                onConfirm({
                  branchId,
                  inventoryId,
                  code,
                  referenceId: selectedReferenceId,
                  referenceCode: selectedReferenceCode
                })
              }
            >
              {canContinue ? (
                <>
                  {/* <i className='fas fa-play-circle me-2'></i> */}
                  {t('Continue')}
                </>
              ) : (
                <>
                  <i className='fas fa-lock me-2'></i>
                  {t('CompleteSelections')}
                </>
              )}
            </button>
          </>
        )}
      </Modal.Footer>

      <style>{`
        .min-vh-75s {
          min-height: 50vh;
        }
        .customcard {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .customcard:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        }
        .table-actived {
          background-color: #f8f9fa;
        }
        .table-active {
          background-color: rgba(13, 110, 253, 0.1) !important;
        }
        .text-purple { color: #6f42c1 !important; }
        .text-cyan { color: #0dcaf0 !important; }
        .text-lime { color: #84cc16 !important; }
        .text-orange { color: #fd7e14 !important; }
        .text-indigo { color: #6610f2 !important; }
        .text-teal { color: #20c997 !important; }
        .text-sky { color: #0ea5e9 !important; }
        .text-violet { color: #8b5cf6 !important; }
        .text-emerald { color: #10b981 !important; }
        .text-stone { color: #78716c !important; }
        .text-yellow { color: #ffc107 !important; }
        .text-blue { color: #0d6efd !important; }
      `}</style>
    </Modal>
  );
};

export default TransactionStartModal;
